import React, { useState, useRef } from 'react';
import Avatar from '@amzn/awsui-chat-components-console/avatar';
import Box from '@amzn/awsui-components-console/box';
import Button from '@amzn/awsui-components-console/button';
import ReactMarkdown from 'react-markdown';
import FeedbackDialog from '../pages/chat/additional-info/feedback-dialog';
import { FeedbackActions } from '../pages/chat/common-components';
import { FeedbackData, FeedbackResponse } from '../services/api';
import { SUGGESTED_RESPONSE_CONTEXT } from '../constants';

const copyToClipboard = (text: string) => {
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();
  try {
    document.execCommand('copy');
  } finally {
    document.body.removeChild(textarea);
  }
};

interface SuggestedResponseBoxProps {
  suggestedResponse: string;
  onCopy?: () => void;
  onFeedbackSubmit?: (data: FeedbackData) => Promise<FeedbackResponse>;
  sessionId?: string | null;
  userMessage?: string;
}

export const SuggestedResponseBox: React.FC<SuggestedResponseBoxProps> = ({
  suggestedResponse,
  onCopy,
  onFeedbackSubmit,
  sessionId,
  userMessage = SUGGESTED_RESPONSE_CONTEXT.DEFAULT
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);
  const [isPositiveFeedback, setIsPositiveFeedback] = useState(false);
  const messageIdRef = useRef(`suggested-response-${Date.now()}`);

  const handleCopy = () => {
    copyToClipboard(suggestedResponse);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    onCopy?.(); // Call optional callback
  };

  return (
    <div
      style={{
        backgroundColor: '#f8f9fa',
        border: '2px solid #e1e4e8',
        borderRadius: '20px',
        padding: '16px 40px 16px 16px',
        boxShadow: isHovered ? '0 4px 8px rgba(0,0,0,0.15)' : '0 2px 4px rgba(0,0,0,0.1)',
        position: 'relative',
        cursor: showFeedbackDialog ? 'default' : 'pointer',
        transition: 'all 0.2s ease',
      }}
      onClick={showFeedbackDialog ? undefined : handleCopy}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
        <Avatar color="gen-ai" iconName="suggestions" ariaLabel="Suggested response" />
        <Box fontSize="body-s" fontWeight="bold" color="text-label">
          Suggested response
        </Box>
      </div>

      {onFeedbackSubmit && sessionId && (
        <div
          style={{
            position: 'absolute',
            top: '12px',
            right: '12px',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <FeedbackActions
            contentToCopy={suggestedResponse}
            onFeedbackClick={(isPositive) => {
              setIsPositiveFeedback(isPositive);
              setShowFeedbackDialog(true);
            }}
            userMessage={userMessage}
            aiResponse={suggestedResponse}
            sessionId={sessionId}
            messageId={messageIdRef.current}
            hideCopy={true}
          />
        </div>
      )}

      <div
        style={{
          fontSize: '14px',
          lineHeight: '1.6',
          color: '#16191f',
          wordBreak: 'break-word',
        }}
      >
        <ReactMarkdown>{suggestedResponse}</ReactMarkdown>
      </div>
      
      {!showFeedbackDialog && (
        <div
          style={{
            position: 'absolute',
            bottom: '12px',
            right: '12px',
          }}
        >
          <Button
            variant="icon"
            iconName={copied ? 'status-positive' : 'copy'}
            ariaLabel={copied ? 'Copied!' : 'Copy suggested message'}
            onClick={(e) => {
              e.stopPropagation();
              handleCopy();
            }}
          />
        </div>
      )}

      {showFeedbackDialog && onFeedbackSubmit && sessionId && (
        <div style={{ marginTop: '16px', borderTop: '1px solid #e1e4e8', paddingTop: '16px' }}>
          <FeedbackDialog
            onDismiss={() => setShowFeedbackDialog(false)}
            onSubmit={async (data) => {
              try {
                await onFeedbackSubmit(data);
                setShowFeedbackDialog(false);
              } catch (error) {
                console.error('Failed to submit feedback:', error);
                // Keep dialog open on error so user can retry
              }
            }}
            userMessage={userMessage}
            aiResponse={suggestedResponse}
            sessionId={sessionId}
            isPositive={isPositiveFeedback}
          />
        </div>
      )}
    </div>
  );
};
